import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import dispatch from "../../middleware";
import { getPolicyAttachmentCreationOptions } from "../../actions/policy-attachment.action";
import { PolicyAttachmentCreationOptions } from "../../models/underwritingPolicyAttachment";
import Alert from "../../components/alert";
import Modal from "../../components/modal";
import MultiSelect, { MultiSelectOption } from "../../components/searchable-multi-select";
import useScreenName from "../../hooks/useScreenName";
import UnderwritingUiPageShell from "./UnderwritingUiPageShell";
import {
  UNDERWRITING_UI_ROUTE_PATHS,
  buildUnderwritingUiExecutionUrl,
  buildUnderwritingUiPolicyCompareUrl,
  buildUnderwritingUiPolicyDetailUrl,
} from "./underwritingUi.constants";

const EMPTY_CREATION_OPTIONS: PolicyAttachmentCreationOptions = {
  employers: [],
  availablePolicies: [],
};

export default function PoliciesIndexPage() {
  useScreenName("Underwriting UI");
  const history = useHistory();
  const storeDispatch = useDispatch();
  const [creationOptions, setCreationOptions] = useState<PolicyAttachmentCreationOptions>(
    EMPTY_CREATION_OPTIONS
  );
  const [searchText, setSearchText] = useState("");
  const [executionId, setExecutionId] = useState("");
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [basePolicy, setBasePolicy] = useState("");
  const [targetPolicy, setTargetPolicy] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  function openExecutionAudit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedExecutionId = executionId.trim();
    if (!trimmedExecutionId) return;
    history.push(buildUnderwritingUiExecutionUrl(trimmedExecutionId));
  }

  useEffect(() => {
    async function loadPolicies() {
      try {
        setIsLoading(true);
        setError("");
        const response = (await dispatch(
          storeDispatch,
          getPolicyAttachmentCreationOptions()
        )) as PolicyAttachmentCreationOptions | undefined;
        if (!response?.availablePolicies) {
          throw new Error("Failed to load policies");
        }
        setCreationOptions(response);
      } catch (e: any) {
        setError(e.message || "Failed to load policies");
      } finally {
        setIsLoading(false);
      }
    }

    loadPolicies();
  }, [storeDispatch]);

  const policies = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return creationOptions.availablePolicies;
    return creationOptions.availablePolicies.filter((policy) =>
      `${policy.name} ${policy.description}`.toLowerCase().includes(query)
    );
  }, [creationOptions.availablePolicies, searchText]);
  const policyOptions = useMemo(
    () =>
      creationOptions.availablePolicies.map(
        (policy): MultiSelectOption => ({
          value: policy.name,
          text: policy.name,
        })
      ),
    [creationOptions.availablePolicies]
  );
  const basePolicyOptions = useMemo(
    () =>
      policyOptions.map((option) => ({
        ...option,
        disabled: option.value === targetPolicy,
      })),
    [policyOptions, targetPolicy]
  );
  const targetPolicyOptions = useMemo(
    () =>
      policyOptions.map((option) => ({
        ...option,
        disabled: option.value === basePolicy,
      })),
    [policyOptions, basePolicy]
  );
  const canCompare = Boolean(basePolicy) && Boolean(targetPolicy) && basePolicy !== targetPolicy;

  return (
    <UnderwritingUiPageShell title="Policies" subtitle="Underwriting service views">
      <div style={{ padding: 16 }}>
        <form className="mb-4" onSubmit={openExecutionAudit}>
          <label className="form-label" htmlFor="underwriting-execution-id">
            Open execution audit
          </label>
          <div className="input-group">
            <input
              id="underwriting-execution-id"
              className="form-control"
              placeholder="Enter execution ID"
              value={executionId}
              onChange={(event) => setExecutionId(event.target.value)}
            />
            <button
              className="btn btn-outline-primary"
              type="submit"
              disabled={!executionId.trim()}
            >
              Open
            </button>
          </div>
        </form>
        {isLoading ? (
          <div>Loading policy list...</div>
        ) : error ? (
          <Alert type="danger" message={error} />
        ) : creationOptions.availablePolicies.length === 0 ? (
          <div>No policies found</div>
        ) : (
          <>
            <div className="mb-3 d-flex justify-content-end">
              <button
                className="btn btn-outline-primary"
                style={{ marginRight: 8 }}
                type="button"
                onClick={() => {
                  setBasePolicy("");
                  setTargetPolicy("");
                  setIsCompareModalOpen(true);
                }}
              >
                Compare policies
              </button>
              <Link
                className="btn btn-primary"
                to={UNDERWRITING_UI_ROUTE_PATHS.POLICY_SIMULATE}
              >
                Run simulation
              </Link>
            </div>
            <input
              className="form-control mb-3"
              placeholder="Search policies"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle">
                <thead>
                  <tr>
                    <th>Policy name</th>
                    <th>Description</th>
                    <th style={{ width: 140 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((policy) => (
                    <tr key={policy.name}>
                      <td>
                        <Link to={buildUnderwritingUiPolicyDetailUrl(policy.name)}>
                          {policy.name}
                        </Link>
                      </td>
                      <td>{policy.description || "—"}</td>
                      <td>
                        <Link
                          className="btn btn-sm btn-outline-primary"
                          to={buildUnderwritingUiPolicyDetailUrl(policy.name)}
                        >
                          View details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <Modal
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        onSubmit={() => {
          if (canCompare) {
            history.push(buildUnderwritingUiPolicyCompareUrl(basePolicy, targetPolicy));
            setIsCompareModalOpen(false);
          }
        }}
        title="Compare policies"
        hasSubmitButton
        hasCancelButton
        submitButtonText="Compare"
        submitButtonDisable={!canCompare}
      >
        <div className="mb-3">
          <label className="form-label">Policy A</label>
          <MultiSelect
            options={basePolicyOptions}
            value={basePolicy ? [basePolicy] : []}
            onChange={(values) => setBasePolicy(String(values[0] ?? ""))}
            placeholder="Select policy A"
            maxSelections={1}
            selectAll={false}
            clearable
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Policy B</label>
          <MultiSelect
            options={targetPolicyOptions}
            value={targetPolicy ? [targetPolicy] : []}
            onChange={(values) => setTargetPolicy(String(values[0] ?? ""))}
            placeholder="Select policy B"
            maxSelections={1}
            selectAll={false}
            clearable
          />
        </div>
      </Modal>
    </UnderwritingUiPageShell>
  );
}
